import { useState } from "react";
import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { MapPin, Users, Megaphone, HandHeart, CheckCircle2, Loader2, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPPORTUNITIES = [
  {
    icon: MapPin,
    title: "List Your Venue",
    description:
      "If your business is sensory-friendly — quiet spaces, dimmed lighting, ear defenders available — get listed on our Community Places directory and reach people actively looking for spaces like yours.",
    color: "bg-brand-teal/10",
    iconColor: "text-brand-teal",
  },
  {
    icon: Users,
    title: "Partner With Us",
    description:
      "We're looking for partnerships with organisations supporting neurodivergent communities — charities, SEND services, local authorities, schools, and support groups.",
    color: "bg-brand-purple/10",
    iconColor: "text-brand-purple",
  },
  {
    icon: Megaphone,
    title: "Promote Your Service",
    description:
      "Reach an engaged audience of neurodivergent individuals, parents, and carers through our Support & Resources section. We feature services that genuinely help — no aggressive ads, ever.",
    color: "bg-brand-peach/15",
    iconColor: "text-[#D4956A]",
  },
  {
    icon: HandHeart,
    title: "Sponsor the App",
    description:
      "Support NeuroNest's mission to make sensory-safe tools accessible to everyone. Sponsors help us continue developing new features, expanding Community Places worldwide, and keeping costs low for the people who need it most.",
    color: "bg-brand-sage/20",
    iconColor: "text-[#7EAD87]",
  },
];

export default function PartnersPage() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", interest: "", message: "" });
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setState("loading");
    try {
      await axios.post(`${API}/partner-inquiry`, form);
      setState("success");
      setMsg("Thanks for reaching out! We'll be in touch soon.");
      setForm({ name: "", email: "", organisation: "", interest: "", message: "" });
    } catch {
      setState("error");
      setMsg("Something went wrong. Please try again or email us directly.");
      setTimeout(() => setState("idle"), 4000);
    }
  };

  return (
    <div data-testid="partners-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Partner With Us"
        description="List your sensory-friendly venue, partner with NeuroNest, promote your SEND service, or sponsor the app. Let's build a more sensory-friendly world together."
        path="/partners"
      />
      <Navbar />

      <main className="pt-28 pb-20">
        {/* Hero */}
        <section className="px-6 md:px-12 lg:px-20 mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm uppercase tracking-widest text-brand-teal font-semibold mb-4">
              Work with us
            </p>
            <h1
              data-testid="partners-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-6"
            >
              Let's build a more sensory-friendly world together
            </h1>
            <p className="text-base md:text-lg text-[#5A6376] leading-relaxed max-w-2xl mx-auto">
              Whether you run a sensory-friendly venue, support neurodivergent
              communities, or want to help keep NeuroNest free — we'd love to
              hear from you.
            </p>
          </div>
        </section>

        {/* Opportunities */}
        <section className="px-6 md:px-12 lg:px-20 mb-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
            {OPPORTUNITIES.map((opp, i) => (
              <div
                key={opp.title}
                data-testid={`opportunity-card-${i}`}
                className="bg-white rounded-3xl border border-[#E7E9EC] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
              >
                <div
                  className={`w-12 h-12 rounded-2xl ${opp.color} flex items-center justify-center mb-5`}
                >
                  <opp.icon size={22} className={opp.iconColor} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mb-2">
                  {opp.title}
                </h3>
                <p className="text-sm text-[#5A6376] leading-relaxed">
                  {opp.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#2F3A56] mb-3">
                Get in touch
              </h2>
              <p className="text-sm text-[#5A6376]">
                Or email us directly at{" "}
                <a href="mailto:hello@neuronest.global" className="text-brand-teal hover:underline">
                  hello@neuronest.global
                </a>
              </p>
            </div>

            {state === "success" ? (
              <div
                data-testid="partner-form-success"
                className="flex items-center gap-3 justify-center bg-brand-sage/20 rounded-2xl p-6"
              >
                <CheckCircle2 size={20} className="text-[#7EAD87]" />
                <span className="text-sm font-medium text-[#2F3A56]">{msg}</span>
              </div>
            ) : (
              <form
                data-testid="partner-inquiry-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl border border-[#E7E9EC] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)] space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">Name *</label>
                    <input
                      data-testid="partner-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] placeholder:text-[#8B95A5] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">Email *</label>
                    <input
                      data-testid="partner-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] placeholder:text-[#8B95A5] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">Organisation</label>
                    <input
                      data-testid="partner-org"
                      type="text"
                      value={form.organisation}
                      onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                      className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] placeholder:text-[#8B95A5] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all"
                      placeholder="Your organisation"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">I'm interested in</label>
                    <select
                      data-testid="partner-interest"
                      value={form.interest}
                      onChange={(e) => setForm({ ...form, interest: e.target.value })}
                      className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all"
                    >
                      <option value="">Select an option</option>
                      <option value="listing">Listing my venue</option>
                      <option value="partnership">Partnership</option>
                      <option value="promotion">Promoting my service</option>
                      <option value="sponsorship">Sponsorship</option>
                      <option value="other">Something else</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">Message *</label>
                  <textarea
                    data-testid="partner-message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] placeholder:text-[#8B95A5] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all resize-none"
                    placeholder="Tell us a bit about what you have in mind..."
                  />
                </div>
                {state === "error" && (
                  <p className="text-xs text-brand-rose">{msg}</p>
                )}
                <Button
                  data-testid="partner-submit"
                  type="submit"
                  disabled={state === "loading"}
                  className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white px-8 py-3 text-sm font-semibold shadow-lg shadow-brand-teal/20 transition-all duration-300 hover:-translate-y-0.5 h-auto w-full sm:w-auto disabled:opacity-70"
                >
                  {state === "loading" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send size={14} /> Send Inquiry
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
